import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Snackbar,
  Container,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { useTranslation } from "react-i18next";
import bg_profile from "../../assets/img/bg_profile.svg";
import SmartHireLoader from "components/Loading/SmartHireLoader";
import { useSnackbar } from "hooks/useSbackbar";
import RecruiterPasswordSettings from "views/user/RecruiterPasswordSettings";

const ProfileCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#fff",
  borderRadius: "15px",
  boxShadow: "0 4px 50px #0000001A",
  width: "80%",
  margin: "0 auto",
  maxWidth: "1200px",
}));

const ProfileImageContainer = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
});

const StyledTextField = styled(TextField)(({ theme, isediting }) => ({
  "& .MuiOutlinedInput-root": {
    height: 56,
    borderRadius: "10px",
    fontFamily: "Poppins",
    borderColor: "#00000033",
    backgroundColor: "#fff",
    ...(isediting === "true" && {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        borderWidth: "2px",
        boxShadow: "0px 0px 5px 0.4px #E700C8",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        borderWidth: "2px",
        boxShadow: "0px 0px 5px 0.4px #E700C8",
      },
    }),
    ...(isediting === "false" && {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#00000033",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#00000033",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
    }),
  },
}));

const StyledTextArea = styled(TextField)(({ theme, isediting }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: isediting === "false" ? "#f5f5f5" : "#fff",
    ...(isediting === "true" && {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        borderWidth: "2px",
        boxShadow: "0px 0px 5px 0.4px #E700C8",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
        borderWidth: "2px",
        boxShadow: "0px 0px 5px 0.4px #E700C8",
      },
    }),
    ...(isediting === "false" && {
      backgroundColor: "#fff",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#00000033",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#00000033",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
    }),
    "&.Mui-disabled": {
      backgroundColor: "#fff",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    padding: "16px",
    lineHeight: "1.5",
    fontSize: "1rem",
    color: "#333",
  },
}));

const StyledTypography = styled(Typography)(() => ({
  fontFamily: "Poppins",
  color: "#00000066",
  opacity: "80%",
  fontSize: "14px",
}));

const UpdateButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#004F98",
  boxShadow: "0px 0px 13px 4px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: "#0085FF",
    color: "#fff",
  },
}));

const AdminProfile = ({ updateAdminData }) => {
  console.log('AdminProfile component loaded with updateAdminData:', typeof updateAdminData);
  const [formData, setFormData] = useState({
    // company: "",
    email: "",
    // country: "",
    // city: "",
    // zip_code: "",
    // address: "",
    first_name: "",
    image: "",
    last_name: "",
    // comp_industry: "",
    // comp_description: "",
    // company_logo: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  // const [showSnackbar, setShowSnackbar] = useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { showSnackbar } = useSnackbar();
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      //console.log("Access token : ", decodedToken);
      const userId = decodedToken.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    //console.log("payload : ", jsonPayload);

    return JSON.parse(jsonPayload);
  }

  const userId = getUserIdFromAccessToken();

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}authentication/admins/${userId}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      console.log("response2126", response.data.image);
      const { id, image, ...filteredData } = response.data;
      setFormData({
        ...filteredData,
        company_logo: response.data.image || "",
      });
      setLoading(false);
    } catch (err) {
      setError(t("general.error"));
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("formData22", formData);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("upload_preset", process.env.REACT_APP_PRESET_KEY);

    try {
      // setLoading(true);
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload`,
        {
          method: "POST",
          body: formDataUpload,
        }
      );
      const data = await response.json();
      const updatedFormData = { ...formData, company_logo: data.secure_url };
      setFormData(updatedFormData);
      // showSnackbar("Image uploaded successfully!", "success");
      // handleSave()
      // Update admin data in parent component (for navbar) immediately
      if (updateAdminData) {
        updateAdminData({
          company: updatedFormData.company,
          email: updatedFormData.email,
          first_name: updatedFormData.first_name,
          last_name: updatedFormData.last_name,
          company_logo: data.secure_url,
          image: data.secure_url,
          created_by: {
            company_logo: data.secure_url
          }
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showSnackbar("Error uploading image", "error");
      setError("Error uploading image");
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = () => fileInputRef.current.click();

  const handleEdit = () => {
    setIsEditing(true);
  };
  const [severity, setSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  const showNotification = (message, severity = "success") => {
    setAlertMessage(message);
    setSeverity(severity);
    showSnackbar(message, { severity: severity });
  };
  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await axios.put(`${apiUrl}authentication/admins/${userId}/`, {
        ...formData,
        image: formData.company_logo,
      }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      // showSnackbar(t("adminProfile.snackbarMessage"), "success");
      showNotification(t("adminProfile.snackbarMessage"), "success");

      console.log('✅ Profile updated successfully, updating navbar...');
      // Update admin data in parent component (for navbar)
      if (updateAdminData) {
        const updateData = {
          company: formData.company,
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          company_logo: formData.company_logo,
          image: formData.company_logo,
          created_by: {
            company_logo: formData.company_logo
          }
        };
        updateAdminData(updateData);
      } else {
        console.error('❌ updateAdminData function not available');
      }

      setIsEditing(false);
      setLoading(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      showSnackbar(t("general.error") || "Error updating profile", "error");
      setError(t("general.error"));
      setLoading(false);
    }
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <>
      <Box sx={{ position: "relative", width: "100%" }}>
        {/* Background Container */}
        <Box
          sx={{
            position: "relative",
            minHeight: "80px",
            overflow: "hidden",
            backgroundImage: `url(${bg_profile})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        ></Box>

        {/* Content Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            position: "relative",
            marginTop: "-100px",
          }}
        >
          {/* Profile Info Container */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginTop: "25px",
              width: "80%",
              maxWidth: "1200px",
              px: { xs: "16px", md: "1%" },
              pb: "2%",
            }}
          >
            <Box position="relative">
              <Box
                sx={{
                  width: "200px",
                  height: "200px",
                  border: "8px solid white",
                  borderRadius: "50%",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: 'linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)',
                }}
              >
                {formData.company_logo ? (
                  <img
                    src={formData.company_logo}
                    alt={formData.company || "Company Logo"}
                    style={{
                      maxWidth: "140%",
                      maxHeight: "140%",
                      objectFit: "contain",
                      transform: "scale(0.8)",
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "48px",
                      fontWeight: "bold",
                      color: "#020060",
                    }}
                  >
                    {formData.company?.charAt(0)?.toUpperCase() || "A"}
                  </Typography>
                )}
              </Box>
              {isEditing && <IconButton
                onClick={handleImageClick}
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 15,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(4px)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <CameraAltIcon sx={{ fontSize: 20 }} />
              </IconButton>}

              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Box>

            {/* Text Content */}
            <ProfileImageContainer>
              <Box sx={{ pt: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#000000",
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: '32px',
                  }}
                >
                  {formData.first_name} {formData.last_name}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    color: "#00000066",
                    fontSize: "18px",
                    fontFamily: "Poppins",
                  }}
                >
                  {t("navbar.administrator")}
                </Typography>
              </Box>
            </ProfileImageContainer>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            zIndex: 9999,
          }}
        >
          <SmartHireLoader />
        </Box>
      ) : (
        <ProfileCard elevation={0}>
          {/* Profile Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-between",
              mb: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "22px",
                color: "#1a237e",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {t("adminProfile.title")}
            </Typography>

            <Button
              variant="outlined"
              onClick={handleEdit}
              disabled={isEditing}
              sx={{
                py: 1,
                border: "none",
                borderRadius: "10px",
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "#004F98",
                boxShadow: "0px 4px 50px 0px #0000001A",
                "&:hover": {
                  border: "none",
                  backgroundColor: "rgba(26, 35, 126, 0.04)",
                },
              }}
            >
              {t("adminProfile.editButton")} <DriveFileRenameOutlineIcon />
            </Button>
          </Box>

          {/* Form Fields */}
          <Grid
            container
            columnSpacing={5}
            rowSpacing={6}
            sx={{
              pl: 5,
              pr: 5,
            }}
          >
            {Object.entries(formData)
              .filter(([key]) => key == "first_name" || key == "last_name" || key == "email")
              .map(([key, value]) => (
                <Grid item xs={12} md={5} key={key}>
                  <FormControl fullWidth>
                    <StyledTypography
                      sx={{ color: "grey", opacity: "80%", fontSize: "14px" }}
                    >
                      {t(`adminProfile.fields.${key}`)}
                    </StyledTypography>

                    <StyledTextField
                      fullWidth
                      name={key}
                      value={value || ""}
                      onChange={handleChange}
                      disabled={!isEditing}
                      variant="outlined"
                      isediting={isEditing.toString()}
                    />

                  </FormControl>
                </Grid>
              ))}
          </Grid>

          {/* Update Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <UpdateButton
              variant="contained"
              onClick={handleSave}
              disabled={!isEditing || loading}
              sx={{
                bgcolor: "#fff",
                color: "#004F98",
                borderRadius: "10px",
                textTransform: "none",
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                fontWeight: "500px",
                "&:hover": {
                  bgcolor: "#0085FF",
                },
                "&.Mui-disabled": {
                  bgcolor: "#e0e0e0",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>{t("adminProfile.updating") || "Updating..."}</span>
                </Box>
              ) : (
                t("adminProfile.updateButton")
              )}
            </UpdateButton>
          </Box>
          <Grid container sx={{ marginTop: { xs: 4, md: 8 } }}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "22px",
                  color: "#1a237e",
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                {t("personalInformation.password_settings")}
              </Typography>
            </Grid>
            <Grid item xs={12}
              sx={{
                ml: 3
              }}>
              <RecruiterPasswordSettings
                formData={formData}
                setFormData={setFormData}
              />
            </Grid>
          </Grid>
        </ProfileCard>
      )}

      {/* Snackbar */}
      {/* <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(3px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
          },
        }}
        message={t("adminProfile.snackbarMessage")}
      /> */}
    </>
  );
};

export default AdminProfile;